import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { Divider } from "primereact/divider";
import Helpers from "../../utils/helpers";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo, useRef } from "react";
import OrderService from "../../services/order/order.service";

const NewCreateInvoiceTotal = (props: any) => {
  const { t } = useTranslation();
  const [disAmt, setDisAmt] = useState<number>(0);
  const [availableRewardPoints, setAvailableRewardPoints] = useState(0);
  const [rewardToCurrencyRatio, setRewardToCurrencyRatio] = useState(0);
  const [useRewards, setUseRewards] = useState(false);
  const [usedRewardPoints, setUsedRewardPoints] = useState(0);
  const invoicedRewardPoint = props.orderedItems?.invoicedRewardPoint ?? 0;
  // Outstanding points from the original order that MUST be redeemed (have not been invoiced yet)
  const unfulfilledOrderPoints = Math.max(0, usedRewardPoints - invoicedRewardPoint);

  const pointsUsed = props.orderedItems?.rewardPointsUsed ?? props.orderedItems?.rewardPoints ?? 0;
  const rewardPointAmount = props.orderedItems?.rewardPointAmount ?? 0;
  const usedRewardDuringOrdering = pointsUsed > 0 || rewardPointAmount > 0;

  const hasPersistedRewardUsage = unfulfilledOrderPoints > 0;
  const { onRewardChange, paymentAlreadyCharged } = props;
  // Track if user has manually toggled the switch — prevents orderedItems re-renders from overriding it
  const hasUserToggledRef = useRef(false);

  const subTotal = useMemo(() => {
    const orderedItems = props.orderedItems || {};
    const hasOrderInvoice = orderedItems.invoices && orderedItems.invoices.length > 0;
    return (props.taxableAmount || 0) +
      (props.taxAmount || 0) +
      (hasOrderInvoice
        ? 0
        : (orderedItems.handlingCharges || 0) +
        (orderedItems.courierCharges || 0) +
        (orderedItems.packingCharges || 0) +
        (orderedItems.paymentGatewayCharges || 0));
  }, [props.taxableAmount, props.taxAmount, props.orderedItems]);

  const totalAmount = useMemo(() => {
    return Math.max(0, subTotal - disAmt);
  }, [subTotal, disAmt]);

  // Check if there are extra points being needed beyond the remaining unfulfilled order points
  const extraPointsNeeded = useMemo(() => {
    if (rewardToCurrencyRatio <= 0) return 0;
    const totalPointsNeeded = Math.round(totalAmount / rewardToCurrencyRatio + Number.EPSILON);
    const maxRedeemable = availableRewardPoints + unfulfilledOrderPoints;
    const pointsToRedeem = Math.min(maxRedeemable, totalPointsNeeded);
    return Math.max(pointsToRedeem - unfulfilledOrderPoints, 0);
  }, [totalAmount, rewardToCurrencyRatio, availableRewardPoints, unfulfilledOrderPoints]);

  // Determine if switch should be disabled when trying to turn off
  // We can toggle off IF the payment has not been charged yet (e.g. COD/Cash), OR if there are extra points needed, OR if there are no unfulfilled points left.
  const canToggleOff = !paymentAlreadyCharged || extraPointsNeeded > 0 || unfulfilledOrderPoints === 0;

  // Auto-enforce useRewards to true if it cannot be toggled off (e.g., user decreased qty back to normal)
  useEffect(() => {
    if (!canToggleOff && !useRewards && unfulfilledOrderPoints > 0) {
      setUseRewards(true);
      hasUserToggledRef.current = false; // Reset user interaction flag
    }
  }, [canToggleOff, useRewards, unfulfilledOrderPoints]);

  useEffect(() => {
    if (!props.orderedItems) return;
    const pointsUsed = props.orderedItems.rewardPointsUsed ?? props.orderedItems.rewardPoints ?? 0;
    const invoicePointsUsed = props.orderedItems.invoicedRewardPoint ?? 0;
    const rewardAmt = props.orderedItems.rewardPointAmount ?? 0;

    const ratioUsed = props.orderedItems.rewardToCurrencyRatio ?? props.orderedItems.rewardsToCurrencyRatio ?? 0;
    setUsedRewardPoints(pointsUsed);
    if (ratioUsed > 0) setRewardToCurrencyRatio(ratioUsed);

    // Auto-enable rewards if points were used in the original order OR if points were used in previous invoices
    if ((pointsUsed > 0 || invoicePointsUsed > 0 || rewardAmt > 0) && !hasUserToggledRef.current) {
      setUseRewards(true);
    } else if (!hasUserToggledRef.current) {
      setUseRewards(false);
    }
  }, [props.orderedItems]);

  useEffect(() => {
    if (usedRewardDuringOrdering && !useRewards) {
      setUseRewards(true);
    }
  }, [usedRewardDuringOrdering, useRewards]);

  useEffect(() => {
    if (!props.orderedItems?.userID) return;
    if (rewardToCurrencyRatio > 0 && availableRewardPoints > 0) return;
    const fetchRewards = async () => {
      try {
        const res = await OrderService.getUserRewardBalance(
          props.orderedItems.userID
        );
        setAvailableRewardPoints(res?.availablePoints ?? 0);
        setRewardToCurrencyRatio(res?.rewardToCurrencyRatio ?? 0);
        props.setRewardRatio?.(res?.rewardToCurrencyRatio ?? 0);

        if ((res?.availablePoints ?? 0) <= 0) setUseRewards(false);
      } catch (error) {
        setUseRewards(false);
      }
    };
    fetchRewards();
  }, [props.orderedItems?.userID]);

  /* ---------------- REWARD CALCULATION (DERIVED) ---------------- */
  const rewardsPointsRedeemed = useMemo(() => {
    if (!useRewards || rewardToCurrencyRatio <= 0)
      return paymentAlreadyCharged ? Math.max(0, unfulfilledOrderPoints) : 0;

    const totalPointsNeeded = Math.max(0, Math.round(totalAmount / rewardToCurrencyRatio + Number.EPSILON));
    const maxRedeemable = Math.max(0, availableRewardPoints + unfulfilledOrderPoints);

    return Math.max(0, Math.min(maxRedeemable, totalPointsNeeded));
  }, [
    useRewards,
    totalAmount,
    rewardToCurrencyRatio,
    availableRewardPoints,
    unfulfilledOrderPoints,
    paymentAlreadyCharged
  ]);

  /* ---------------- FINAL TOTAL ---------------- */

  const rewardDiscountAmount = rewardsPointsRedeemed * rewardToCurrencyRatio;

  const finalTotal = Math.max(Math.round(totalAmount - rewardDiscountAmount), 0);

  const remainingPoints = useMemo(() => {
    return Math.max(
      availableRewardPoints + unfulfilledOrderPoints - rewardsPointsRedeemed,
      0
    );
  }, [availableRewardPoints, unfulfilledOrderPoints, rewardsPointsRedeemed]);

  useEffect(() => {
    onRewardChange?.({
      redeemedPoints: rewardsPointsRedeemed,
      finalPayable: finalTotal,
      switchEnabled: useRewards
    });
  }, [rewardsPointsRedeemed, rewardDiscountAmount, finalTotal, useRewards]);

  /* ---------------- DISCOUNT ---------------- */

  const handleUpdateDiscount = (value: number) => {
    let v = value || 0;
    if (v > subTotal) {
      v = subTotal;
    }
    setDisAmt(v);
    props.updateDiscountAmount(v);
  };

  const formatAddress = () => {
    const orderedItems = props.orderedItems || {};
    const {
      address1,
      address2,
      landmark,
      areaName,
      subDistrictName,
      districtName,
      stateName,
      zipcode
    } = orderedItems;

    // First line components
    const line1Parts = [
      address1,
      address2,
      landmark
    ].filter(Boolean);

    // Second line components (area and sub-district)
    const line2Parts = [
      areaName,
      subDistrictName,
      districtName
    ].filter(Boolean);

    // Third line components (district, state, and pin)
    const line3Parts = [
      stateName,
      zipcode && `Pin: ${zipcode}`
    ].filter(Boolean);

    return (
      <>
        {line1Parts.length > 0 && <>{line1Parts.join(", ")}</>}
        {(line2Parts.length > 0 || line3Parts.length > 0) && (
          <>
            <br />
            {line2Parts.length > 0 && line2Parts.join(", ")}
            {line3Parts.length > 0 && (
              <>
                {line2Parts.length > 0 && ", "}
                {line3Parts.join(", ")}
              </>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="flex flex-wrap align-items-left justify-content-between px-3">
      <div className="col-12 md:col-6 lg:col-6">
        <label style={{ color: 'green' }}>{t("generateInv_shipping_add")}: </label>
        <p>{formatAddress()}</p>
        <Divider className="my-4" />
        {/* REWARDS INTERFACE - Hidden when payment already charged */}
        {!paymentAlreadyCharged && (
          <div
            className="p-3 border-round-lg border-1"
            style={{
              background: useRewards ? '#f0fdf4' : '#fafafa',
              borderColor: useRewards ? '#bbf7d0' : '#dee2e6'
            }}
          >
            <div className="flex align-items-center justify-content-between mb-3">
              <div className="flex align-items-center gap-2">
                <span className="text-xl">🎁</span>
                <div>
                  <span className="font-bold text-800 text-sm uppercase tracking-wider block">Reward Points</span>
                  {rewardToCurrencyRatio > 0 && (
                    <span className="text-xs text-600 font-medium">
                      1 Point = {Helpers.formatAmountInINR(rewardToCurrencyRatio)}
                    </span>
                  )}
                </div>
              </div>
              <InputSwitch
                checked={useRewards}
                onChange={(e) => {
                  const newValue = e.value ?? false;
                  // If trying to turn off and only order points are being used, prevent it
                  if (!newValue && !canToggleOff && unfulfilledOrderPoints > 0) {
                    // Show message that these points were added during ordering
                    return;
                  }
                  hasUserToggledRef.current = true; // mark that user has manually changed the switch
                  setUseRewards(newValue);
                }}
                disabled={
                  usedRewardDuringOrdering ||
                  (availableRewardPoints <= 0 && unfulfilledOrderPoints <= 0) ||
                  !canToggleOff
                }
              />
            </div>

            {/* Message when rewards were applied during ordering */}
            {usedRewardDuringOrdering && (
              <div className="mb-3 p-2 border-round" style={{ backgroundColor: '#e0f2fe', border: '1px solid #7dd3fc' }}>
                <p className="m-0 text-xs text-700" style={{ color: '#0369a1' }}>
                  <i className="pi pi-info-circle mr-2"></i>
                  Reward points were applied during order placement and cannot be modified.
                </p>
              </div>
            )}

            {/* Message when only order points are being used */}
            {useRewards && !usedRewardDuringOrdering && !canToggleOff && unfulfilledOrderPoints > 0 && (
              <div className="mb-3 p-2 border-round" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                <p className="m-0 text-xs text-700">
                  <i className="pi pi-info-circle mr-2"></i>
                  These points were added while ordering and cannot be removed from this invoice.
                </p>
              </div>
            )}

            {/* Row 1: Remaining & Used While Order */}
            <div className="flex justify-content-between align-items-end mb-3">
              <div>
                <p className="m-0 text-600 text-xs font-bold uppercase">Remaining</p>
                <p className="m-0 text-xl font-bold text-900">
                  {remainingPoints.toLocaleString()} <span className="text-xs font-medium text-500">pts</span>
                </p>
              </div>

              <div className="text-right">
                <p className="m-0 text-600 text-xs font-bold uppercase">
                  Used While Order
                </p>
                <p className="m-0 text-xl font-bold text-green-600">
                  {usedRewardPoints.toLocaleString()} <span className="text-xs font-medium text-500">pts</span>
                </p>
              </div>
            </div>

            {/* Row 2: Invoice Usage & Redeeming */}
            <div className="flex justify-content-between align-items-end pt-3 border-top-1" style={{ borderColor: useRewards ? '#bbf7d0' : '#dee2e6' }}>
              <div>
                <p className="m-0 text-600 text-xs font-bold uppercase">Last Invoice Usage</p>
                <p className="m-0 text-xl font-bold text-orange-500">
                  {props.orderedItems?.invoicedRewardPoint?.toLocaleString() ?? '0'} <span className="text-xs font-medium text-500">pts</span>
                </p>
              </div>

              <div className="text-right">
                <p className="m-0 text-600 text-xs font-bold uppercase">Redeeming</p>
                <p className={`m-0 text-xl font-bold ${rewardsPointsRedeemed > 0 ? 'text-green-600' : 'text-400'}`}>
                  {rewardsPointsRedeemed.toLocaleString()} <span className="text-xs font-medium text-500">pts</span>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="col-12 md:col-6 lg:col-6">
        <div className="flex flex-wrap">
          <div className="col-6 md:col-6 lg:col-6">
            <p className="m-1" style={{ lineHeight: "1.5" }}>{t("ordersummary_total_items_cost")} :</p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>{t("ordersummary_tax")} :</p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>Delivery Handling :</p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>{t("couriercharge__Breadcrumb_label")} :</p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>Packing Charges :</p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>Payment Gateway :</p>
            {paymentAlreadyCharged && rewardDiscountAmount > 0 && (
              <p className="m-1" style={{ lineHeight: "1.5" }}>Reward Discount :</p>
            )}
            <p className="m-1" style={{ lineHeight: "1.5" }}>Discount Amount :</p>
            <p className="m-1 font-semibold text-green font-bold" style={{ lineHeight: "1.5" }}>
              {t("ordersummary_total")} :
              {rewardsPointsRedeemed > 0 && (
                <span className="text-xs font-bold text-green-600 uppercase block">
                  You saved {Helpers.formatAmountInINR(rewardDiscountAmount)}!
                </span>
              )}
            </p>
          </div>
          <div className="col-6 md:col-6 lg:col-6 text-right">
            <p className="m-1" style={{ lineHeight: "1.5" }}>{Helpers.formatAmountInINR(props.taxableAmount || 0)}</p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>{Helpers.formatAmountInINR(props.taxAmount || 0)}</p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>
              {props.orderedItems?.orderInvoice?.length > 0 ? Helpers.formatAmountInINR(0) : Helpers.formatAmountInINR(props.orderedItems?.handlingCharges || 0)}
            </p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>
              {props.orderedItems?.orderInvoice?.length > 0 ? Helpers.formatAmountInINR(0) : Helpers.formatAmountInINR(props.orderedItems?.courierCharges || 0)}
            </p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>
              {props.orderedItems?.orderInvoice?.length > 0 ? Helpers.formatAmountInINR(0) : Helpers.formatAmountInINR(props.orderedItems?.packingCharges || 0)}
            </p>
            <p className="m-1" style={{ lineHeight: "1.5" }}>
              {props.orderedItems?.orderInvoice?.length > 0 ? Helpers.formatAmountInINR(0) : Helpers.formatAmountInINR(props.orderedItems?.paymentGatewayCharges || 0)}
            </p>
            {paymentAlreadyCharged && rewardDiscountAmount > 0 && (
              <p className="m-1" style={{ lineHeight: "1.5" }}>
                {Helpers.formatAmountInINR(rewardDiscountAmount)}
              </p>
            )}
            <div className="m-1" style={{ lineHeight: "1.5" }}>
              <InputNumber
                inputId="inputDisAmt"
                value={disAmt}
                onChange={(e: any) => { handleUpdateDiscount(e.value) }}
                minFractionDigits={2}
                locale="en-IN"
                min={0}
                max={subTotal}
                className="w-full"
                style={{ lineHeight: "1.5", border: '1px solid #ced4da', borderRadius: '6px', width: '100%' }}
              />
            </div>
            <div className="m-1 font-semibold text-green font-bold text-right" style={{ lineHeight: "1.5" }}>
              {rewardsPointsRedeemed > 0 && (
                <p className="m-0 text-500 line-through" style={{ fontSize: '0.875rem' }}>
                  {Helpers.formatAmountInINR(Math.round(totalAmount))}
                </p>
              )}
              <p className="m-0 font-bold text-green" style={{ fontSize: '1.125rem' }}>
                {Helpers.formatAmountInINR(finalTotal)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCreateInvoiceTotal;